import { ConfigProvider, Flex, Form, FormInstance, Spin } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { COLOR_GREY_E9, COLOR_ITEM } from '../../const';
import { changeItemsForMoving, changePercentageItemByPercent } from '../../gantChangeData';
import { getMappingIdIndex } from '../../gantTransformData';
import useAccessForItem from '../../hooks/useAccessForItem';
import { useGantItemForm } from '../../hooks/useGantItemForm';
import useStatusEditProject from '../../hooks/useStatusEditProject';
import { ComponentsDispatch, RootState } from '../../store';
import { setterGantItems } from '../../store/slices/gantItemsSlice';
import { setMappingItemIdIndex } from '../../store/slices/gantSlice';
import { ItemType } from '../../types';
import ControlPanelItem from '../formField/controlPanelItem';
import useControlStartEndDate from '../formField/useControlStartEndDate';
import GantItemProgress from '../gantItemProgress';

interface IGantItemWrapperForm {
    form: FormInstance<ItemType>;
}

const GantItemWrapperForm: React.FC<IGantItemWrapperForm> = ({ form }) => {
    const [ItemForm, currentGantItem, itemsList, mappingItemIdIndex] = useGantItemForm();
    const dispatch = useDispatch<ComponentsDispatch>();

    const spinner = useSelector<RootState, boolean>((state) => state.spinner.spinner);

    const updatedItem = currentGantItem ? itemsList[mappingItemIdIndex[currentGantItem?.id]] : undefined;
    const isEditableProject = useStatusEditProject(updatedItem?.projectId);
    const access = useAccessForItem(updatedItem || null, [
        'work-item.all.manage',
        'work-item.owner.manage',
        'work-item.assignee.manage',
    ]);

    const historyDeletedChildrenRef = useRef<Set<string>>(new Set());
    const historyDeletedBondRightRef = useRef<Set<string>>(new Set());

    const bufferItems = useRef<ItemType[]>([]);

    const values = Form.useWatch([], { form, preserve: true });

    useEffect(() => {
        historyDeletedBondRightRef.current = new Set();
        historyDeletedChildrenRef.current = new Set();
    }, [currentGantItem]);

    const moveItems = (itemsForMoving: ItemType[], newItems: ItemType[]) => {
        let updatedItems = [...newItems];
        for (const itemMove of itemsForMoving) {
            updatedItems = changeItemsForMoving(mappingItemIdIndex, 0, 'both', itemMove, updatedItems, []);
            if (itemMove.children?.length) {
                const [filteredChildId] = itemMove.children.filter(
                    (childId) => !historyDeletedChildrenRef.current.has(childId),
                );
                const childItem = updatedItems[mappingItemIdIndex[filteredChildId]];
                if (childItem) {
                    updatedItems = changePercentageItemByPercent(
                        mappingItemIdIndex,
                        childItem?.completionPercentage || 0,
                        childItem!,
                        updatedItems,
                    );
                }
            }
        }
        return updatedItems;
    };

    const saveBufferToStore = () => {
        const newMappingScheduleItemIdIndex = getMappingIdIndex(bufferItems.current);
        dispatch(
            setterGantItems({
                itemsList: bufferItems.current,
            }),
        );

        // dispatch(
        //     setterGant({
        //         countVisibleRow: bufferItems.current.filter((item) => !item.hide).length,
        //     }),
        // );
        dispatch(setMappingItemIdIndex(newMappingScheduleItemIdIndex));
    };

    useControlStartEndDate(form, values, bufferItems, saveBufferToStore, updatedItem);

    const filterDeletedBondRight = () => {
        const bondRight = updatedItem?.bondRight || [];
        return bondRight.filter((bondId) => {
            if (mappingItemIdIndex[bondId] === undefined) {
                !historyDeletedBondRightRef.current.has(bondId) && historyDeletedBondRightRef.current.add(bondId);
                return false;
            }
            return true;
        });
    };

    const filterDeletedChildren = () => {
        const children = updatedItem?.children || [];
        return children.filter((childId) => {
            if (mappingItemIdIndex[childId] === undefined) {
                !historyDeletedChildrenRef.current.has(childId) && historyDeletedChildrenRef.current.add(childId);
                return false;
            }
            return true;
        });
    };

    const setterDataToForm = () => {
        if (itemsList.length && updatedItem) {
            form.setFieldsValue({
                ...updatedItem,
                startDate: dayjs(updatedItem.startDate) as any,
                endDate: dayjs(updatedItem.endDate) as any,
                color: updatedItem.color || COLOR_ITEM,
                bondRight: filterDeletedBondRight(),
                children: filterDeletedChildren(),
                status: updatedItem.status?.toString() as any,
                type: updatedItem.type?.toString() as any,
                priority: updatedItem.priority?.toString() as any,
                phaseId: updatedItem.phaseId?.toString() as any,
                divisionId: updatedItem.divisionId?.toString() as any,
                costCodeId: updatedItem.costCodeId?.toString() as any,
            });
            bufferItems.current = [...itemsList];
        }
    };

    useEffect(setterDataToForm, [currentGantItem]);

    // ToDo we cannot use useEffectBuffer because it lead to strange behaviour on gant chart
    // useEffectBuffer(setterDataToForm, 100, [itemsList, currentGantItem]);

    return (
        <Flex vertical flex={1} style={{ width: '100%', height: '100%' }}>
            <ConfigProvider
                theme={{
                    components: {
                        Progress: {
                            remainingColor: updatedItem?.color,
                        },
                    },
                }}
            >
                <div
                    style={{
                        overflowY: 'auto',
                        width: '100%',
                        marginTop: -2,
                    }}
                >
                    <GantItemProgress updatedItem={updatedItem} zIndex={100} />
                </div>
                <div
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        width: '100%',
                    }}
                >
                    <Spin tip={'Saving...'} spinning={spinner} style={{ width: '100%' }}>
                        <Flex vertical style={{ padding: '16px 24px 0' }}>
                            <ItemForm
                                form={form}
                                currentItem={updatedItem}
                                bufferItems={bufferItems}
                                saveBufferToStore={saveBufferToStore}
                                values={values}
                                historyDeletedBondRight={historyDeletedBondRightRef.current}
                                historyDeletedChildren={historyDeletedChildrenRef.current}
                                moveItems={moveItems}
                            />
                        </Flex>
                    </Spin>
                </div>
                {isEditableProject && access && updatedItem?.roleItem === 'workItem' && (
                    <div
                        style={{
                            padding: '16px 24px',
                            borderTop: `1px solid ${COLOR_GREY_E9}`,
                        }}
                    >
                        <Spin spinning={spinner} size={'small'}>
                            <Flex justify={'center'} gap={20}>
                                <ControlPanelItem
                                    moveItems={moveItems}
                                    historyDeletedChildren={historyDeletedChildrenRef.current}
                                />
                            </Flex>
                        </Spin>
                    </div>
                )}
            </ConfigProvider>
        </Flex>
    );
};

export default GantItemWrapperForm;
